import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, Package, Search, Upload, FileSpreadsheet,
  Download, Check, X, MoreVertical, Copy, AlertTriangle, Merge,
  Filter, SlidersHorizontal, Eye, ChevronDown
} from 'lucide-react';
import { formatCurrency, cn } from '../lib/utils';
import { useIsAdmin } from '../context/AuthContext';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const RECENT_HOURS = 24;

const isRecentProduct = (createdAt) => {
  if (!createdAt) return false;
  const created = new Date(createdAt);
  const now = new Date();
  return (now - created) < RECENT_HOURS * 60 * 60 * 1000;
};

const Products = () => {
  const isAdmin = useIsAdmin();
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [importedProducts, setImportedProducts] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [brandFilter, setBrandFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Multi-select state
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  // Duplicate detection states
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateClusters, setDuplicateClusters] = useState([]);
  const [duplicateStats, setDuplicateStats] = useState(null);
  const [detectingDuplicates, setDetectingDuplicates] = useState(false);
  const [selectedDuplicates, setSelectedDuplicates] = useState(new Set());
  const [primarySelections, setPrimarySelections] = useState({});
  const [merging, setMerging] = useState(false);

  // Delete confirmation modal
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [usageInfo, setUsageInfo] = useState(null);
  const [checkingUsage, setCheckingUsage] = useState(false);

  // Duplicate IDs for highlighting in table
  const [duplicateProductIds, setDuplicateProductIds] = useState(new Set());

  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    size: '',
    hsn_code: '',
    gst_percentage: 12,
    purchase_price: '',
    margin_percentage: 5,
    selling_price: '',
    selling_price_manually_edited: false,
    buyer_item_code: '',
    diameter_mm: '',
    length_mm: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [productsRes, brandsRes] = await Promise.all([
        axios.get(`${API_URL}/products`),
        axios.get(`${API_URL}/products/brands`).catch(() => ({ data: [] }))
      ]);
      setProducts(productsRes.data);
      setBrands(brandsRes.data);
    } catch (error) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  // Advanced filtering
  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      const term = searchTerm.toLowerCase();
      const matchesSearch = !term ||
        p.name.toLowerCase().includes(term) ||
        p.brand.toLowerCase().includes(term) ||
        (p.hsn_code && p.hsn_code.includes(term)) ||
        (p.size && p.size.toLowerCase().includes(term));
      const matchesBrand = brandFilter === 'all' || p.brand === brandFilter;
      return matchesSearch && matchesBrand;
    });
  }, [products, searchTerm, brandFilter]);

  const allSelected = filteredProducts.length > 0 && filteredProducts.every(p => selectedProducts.has(p.id));
  const someSelected = selectedProducts.size > 0;

  // ===== CRUD =====
  // Bidirectional sync between purchase_price ↔ margin_percentage (used as
  // "Markup %") ↔ selling_price. Editing any of the three updates the
  // dependent field(s) immediately; the `selling_price_manually_edited` flag
  // is preserved (it is used elsewhere to decide whether future purchases
  // update the selling price).
  //
  // Formulas:
  //   Selling Price = Purchase Price × (1 + Markup / 100)
  //   Markup %      = ((Selling Price − Purchase Price) / Purchase Price) × 100
  const _recalcSelling = (pp, markup) => {
    const p = parseFloat(pp);
    const m = parseFloat(markup);
    if (!Number.isFinite(p) || !Number.isFinite(m) || p <= 0) return '';
    return (p * (1 + m / 100)).toFixed(2);
  };

  const _recalcMarkup = (pp, sp) => {
    const p = parseFloat(pp);
    const s = parseFloat(sp);
    if (!Number.isFinite(p) || !Number.isFinite(s) || p <= 0) return '';
    return (((s - p) / p) * 100).toFixed(2);
  };

  const handlePurchasePriceChange = (val) => {
    setFormData(prev => {
      const next = { ...prev, purchase_price: val };
      if (!prev.selling_price_manually_edited && val !== '' && prev.margin_percentage !== '') {
        // Not manually overridden → keep Markup, recompute Selling Price
        next.selling_price = _recalcSelling(val, prev.margin_percentage);
      } else if (val !== '' && prev.selling_price !== '') {
        // SP is user-locked → keep Selling Price, recompute Markup
        const m = _recalcMarkup(val, prev.selling_price);
        if (m !== '') next.margin_percentage = m;
      }
      return next;
    });
  };

  const handleMarginChange = (val) => {
    setFormData(prev => {
      const next = { ...prev, margin_percentage: val };
      if (prev.purchase_price !== '' && val !== '') {
        // User edited Markup → Selling Price follows; clear manual-override flag
        next.selling_price = _recalcSelling(prev.purchase_price, val);
        next.selling_price_manually_edited = false;
      }
      return next;
    });
  };

  const handleSellingPriceChange = (val) => {
    setFormData(prev => {
      const next = { ...prev, selling_price: val };
      if (prev.purchase_price !== '' && val !== '') {
        // User edited Selling Price → Markup follows automatically
        const m = _recalcMarkup(prev.purchase_price, val);
        if (m !== '') next.margin_percentage = m;
      }
      // Preserve legacy "manually edited" semantics for the purchase-price flow:
      // any value that deviates from the formula counts as a manual override.
      const computed = _recalcSelling(prev.purchase_price, next.margin_percentage);
      next.selling_price_manually_edited = val !== '' && val !== computed && parseFloat(val).toFixed(2) !== computed;
      return next;
    });
  };

  const resetManualFlag = () => {
    setFormData(prev => ({
      ...prev,
      selling_price_manually_edited: false,
      selling_price: _recalcSelling(prev.purchase_price, prev.margin_percentage) || prev.selling_price,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate optional explicit dimensions (positive numbers only, decimals allowed).
    const parseOptPositive = (v, label) => {
      if (v === '' || v === null || v === undefined) return null;
      const n = parseFloat(v);
      if (!Number.isFinite(n) || n <= 0) {
        toast.error(`${label} must be a positive number`);
        throw new Error(`invalid_${label}`);
      }
      return n;
    };
    let dia, len;
    try {
      dia = parseOptPositive(formData.diameter_mm, 'Diameter');
      len = parseOptPositive(formData.length_mm, 'Length');
    } catch { return; }
    try {
      const payload = {
        ...formData,
        purchase_price: parseFloat(formData.purchase_price),
        selling_price: parseFloat(formData.selling_price),
        gst_percentage: parseFloat(formData.gst_percentage),
        margin_percentage: formData.margin_percentage === '' || formData.margin_percentage === null
          ? null
          : parseFloat(formData.margin_percentage),
        selling_price_manually_edited: !!formData.selling_price_manually_edited,
        diameter_mm: dia,
        length_mm: len,
      };
      if (editingProduct) {
        await axios.put(`${API_URL}/products/${editingProduct.id}`, payload);
        toast.success('Product updated');
      } else {
        await axios.post(`${API_URL}/products`, payload);
        toast.success('Product created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    // Derive margin if missing on legacy records
    const pp = Number(product.purchase_price || 0);
    const sp = Number(product.selling_price || 0);
    const derivedMargin = pp > 0 ? Number((((sp - pp) / pp) * 100).toFixed(2)) : 0;
    setFormData({
      name: product.name,
      brand: product.brand,
      size: product.size,
      hsn_code: product.hsn_code,
      gst_percentage: product.gst_percentage,
      purchase_price: product.purchase_price,
      margin_percentage: product.margin_percentage != null ? product.margin_percentage : derivedMargin,
      selling_price: product.selling_price,
      selling_price_manually_edited: !!product.selling_price_manually_edited,
      buyer_item_code: product.buyer_item_code || '',
      diameter_mm: product.diameter_mm != null ? product.diameter_mm : '',
      length_mm: product.length_mm != null ? product.length_mm : '',
    });
    setIsDialogOpen(true);
  };

  const handleDeleteClick = async (product) => {
    setDeleteTarget(product);
    setCheckingUsage(true);
    setShowDeleteConfirm(true);
    try {
      const res = await axios.post(`${API_URL}/products/check-usage`, [product.id]);
      setUsageInfo(res.data[product.id]);
    } catch {
      setUsageInfo(null);
    } finally {
      setCheckingUsage(false);
    }
  };

  const handleConfirmDelete = async (force = false) => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/products/${deleteTarget.id}?force=${force}`);
      toast.success('Product deleted');
      setSelectedProducts(prev => {
        const s = new Set(prev);
        s.delete(deleteTarget.id);
        return s;
      });
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object' && detail.message) {
        toast.error(detail.message);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to delete');
      }
    } finally {
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      setUsageInfo(null);
    }
  };

  // ===== Multi-select =====
  const handleSelectAll = (checked) => {
    setSelectedProducts(checked ? new Set(filteredProducts.map(p => p.id)) : new Set());
  };

  const handleSelectProduct = (productId, checked) => {
    setSelectedProducts(prev => {
      const s = new Set(prev);
      checked ? s.add(productId) : s.delete(productId);
      return s;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedProducts.size === 0) return;
    if (!window.confirm(`Delete ${selectedProducts.size} product(s)? Products linked to invoices/purchases will be skipped.`)) return;
    setIsDeleting(true);
    try {
      const res = await axios.post(`${API_URL}/products/bulk-delete-safe`, [...selectedProducts]);
      if (res.data.deleted_count > 0) {
        toast.success(`Deleted ${res.data.deleted_count} product(s)`);
      }
      if (res.data.blocked_count > 0) {
        toast.warning(`${res.data.blocked_count} product(s) skipped — linked to invoices/purchases`);
      }
      setSelectedProducts(new Set());
      fetchData();
    } catch {
      toast.error('Bulk delete failed');
    } finally {
      setIsDeleting(false);
    }
  };

  // ===== Duplicate Detection =====
  const handleDetectDuplicates = async () => {
    setDetectingDuplicates(true);
    try {
      const res = await axios.post(`${API_URL}/products/detect-duplicates`);
      setDuplicateClusters(res.data.clusters);
      setDuplicateStats(res.data);
      setSelectedDuplicates(new Set());
      setPrimarySelections({});

      // Mark duplicate IDs for table highlighting
      const dupIds = new Set();
      res.data.clusters.forEach(c => c.products.forEach(p => dupIds.add(p.id)));
      setDuplicateProductIds(dupIds);

      if (res.data.duplicate_clusters === 0) {
        toast.success('No duplicates found — your product list is clean!');
      } else {
        setShowDuplicateModal(true);
      }
    } catch (err) {
      console.error('Duplicate detection error:', err);
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to detect duplicates. Please try again.');
    } finally {
      setDetectingDuplicates(false);
    }
  };

  const handleSetPrimary = (clusterIdx, productId) => {
    setPrimarySelections(prev => ({ ...prev, [clusterIdx]: productId }));
    // Remove primary from selected duplicates
    setSelectedDuplicates(prev => {
      const s = new Set(prev);
      s.delete(productId);
      return s;
    });
  };

  const toggleDuplicateSelection = (productId, clusterIdx) => {
    if (primarySelections[clusterIdx] === productId) return; // Can't select primary as duplicate
    setSelectedDuplicates(prev => {
      const s = new Set(prev);
      s.has(productId) ? s.delete(productId) : s.add(productId);
      return s;
    });
  };

  const handleMergeDuplicates = async () => {
    // Validate: each cluster with selected dups must have a primary
    const clustersToMerge = [];
    duplicateClusters.forEach((cluster, idx) => {
      const dupsInCluster = cluster.products.filter(p => selectedDuplicates.has(p.id));
      if (dupsInCluster.length > 0) {
        const primary = primarySelections[idx];
        if (!primary) {
          toast.error(`Select a primary product for "${cluster.brand} — ${cluster.name}"`);
          return;
        }
        clustersToMerge.push({ primary_id: primary, duplicate_ids: dupsInCluster.map(p => p.id) });
      }
    });

    if (clustersToMerge.length === 0) {
      toast.error('Select duplicates to merge/delete');
      return;
    }

    if (!window.confirm(`This will merge ${selectedDuplicates.size} duplicate(s) into their primary products. Linked records will be reassigned. Continue?`)) return;

    setMerging(true);
    let totalMerged = 0;
    try {
      for (const op of clustersToMerge) {
        const res = await axios.post(`${API_URL}/products/merge`, op);
        totalMerged += res.data.stats.products_deleted;
      }
      toast.success(`Merged ${totalMerged} duplicate(s) successfully`);
      setShowDuplicateModal(false);
      setDuplicateProductIds(new Set());
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Merge failed');
    } finally {
      setMerging(false);
    }
  };

  const handleDeleteSelectedDuplicates = async () => {
    if (selectedDuplicates.size === 0) {
      toast.error('Select duplicates to delete');
      return;
    }
    if (!window.confirm(`Delete ${selectedDuplicates.size} selected duplicate(s)? Products linked to records will be skipped.`)) return;

    setMerging(true);
    try {
      const res = await axios.post(`${API_URL}/products/bulk-delete-safe`, [...selectedDuplicates]);
      if (res.data.deleted_count > 0) toast.success(`Deleted ${res.data.deleted_count} duplicate(s)`);
      if (res.data.blocked_count > 0) toast.warning(`${res.data.blocked_count} skipped — linked to records. Use Merge instead.`);
      setShowDuplicateModal(false);
      setDuplicateProductIds(new Set());
      fetchData();
    } catch {
      toast.error('Delete failed');
    } finally {
      setMerging(false);
    }
  };

  // ===== Import =====
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        const mappedProducts = jsonData.map((row, index) => {
          let gstValue = row['GST %'] || row['GST%'] || row['gst_percentage'] || row['GST'] || row['gst'] || row['Gst %'] || row['Gst'] || '';
          if (typeof gstValue === 'string') gstValue = gstValue.replace('%', '').trim();
          const parsedGst = parseFloat(gstValue);
          const finalGst = !isNaN(parsedGst) && parsedGst >= 0 ? parsedGst : 12;
          return {
            row: index + 1,
            name: row['Product Name'] || row['name'] || '',
            brand: row['Brand'] || row['brand'] || '',
            size: row['Size'] || row['size'] || '',
            hsn_code: String(row['HSN Code'] || row['hsn_code'] || ''),
            gst_percentage: finalGst,
            purchase_price: parseFloat(row['Purchase Price'] || row['purchase_price'] || 0),
            selling_price: parseFloat(row['Selling Price'] || row['selling_price'] || 0),
            valid: true
          };
        });
        mappedProducts.forEach(p => {
          if (!p.name || !p.brand || !p.hsn_code || !p.purchase_price || !p.selling_price) p.valid = false;
        });
        setImportedProducts(mappedProducts);
      };
      reader.readAsArrayBuffer(file);
    } catch {
      toast.error('Failed to parse Excel file');
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleBulkImport = async () => {
    const validProducts = importedProducts.filter(p => p.valid);
    if (validProducts.length === 0) { toast.error('No valid products'); return; }
    setImportLoading(true);
    try {
      const res = await axios.post(`${API_URL}/products/bulk-import`, {
        products: validProducts.map(p => ({
          name: p.name, brand: p.brand, size: p.size, hsn_code: p.hsn_code,
          gst_percentage: p.gst_percentage, purchase_price: p.purchase_price, selling_price: p.selling_price
        }))
      });
      toast.success(`Imported ${res.data.success} products`);
      if (res.data.failed > 0) toast.warning(`${res.data.failed} failed`);
      setIsImportDialogOpen(false);
      setImportedProducts([]);
      await fetchData();
      // Auto-detect duplicates after import
      setTimeout(() => handleDetectDuplicates(), 500);
    } catch {
      toast.error('Import failed');
    } finally {
      setImportLoading(false);
    }
  };

  const downloadTemplate = () => {
    try {
      const template = [
        { 'Product Name': 'Dental Implant 3.5x10', 'Brand': 'Nobel Biocare', 'Size': '3.5mm x 10mm', 'HSN Code': '90211000', 'GST %': 12, 'Purchase Price': 5000, 'Selling Price': 7500 },
        { 'Product Name': 'Dental Implant 4.0x12', 'Brand': 'Straumann', 'Size': '4.0mm x 12mm', 'HSN Code': '90211000', 'GST %': 12, 'Purchase Price': 6000, 'Selling Price': 9000 }
      ];
      const ws = XLSX.utils.json_to_sheet(template);
      ws['!cols'] = [{ wch: 25 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 8 }, { wch: 15 }, { wch: 15 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Products');
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'product_import_template.xlsx';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); URL.revokeObjectURL(url); }, 100);
      toast.success('Template downloaded');
    } catch {
      toast.error('Failed to download template');
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({ name: '', brand: '', size: '', hsn_code: '', gst_percentage: 12, purchase_price: '', margin_percentage: 5, selling_price: '', selling_price_manually_edited: false, buyer_item_code: '', diameter_mm: '', length_mm: '' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="page-header mb-0">
          <h1 className="page-title">Products</h1>
          <p className="page-subtitle">Manage your internal product master and inventory identity</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={handleDetectDuplicates}
              disabled={detectingDuplicates}
              data-testid="detect-duplicates-btn"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              <Copy className="w-4 h-4 mr-2" />
              {detectingDuplicates ? 'Scanning...' : 'Detect Duplicates'}
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsImportDialogOpen(true)}
              data-testid="import-products-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Bulk Import
            </Button>
            <Button
              onClick={() => { resetForm(); setIsDialogOpen(true); }}
              className="btn-primary"
              data-testid="add-product-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Product
            </Button>
          </div>
        )}
      </div>

      {/* Search, Filters & Bulk Actions */}
      <Card className="border border-slate-200">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, brand, HSN, size..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="product-search-input"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              data-testid="toggle-filters-btn"
              className={cn(showFilters && 'bg-slate-100')}
            >
              <SlidersHorizontal className="w-4 h-4 mr-1" />
              Filters
              {brandFilter !== 'all' && (
                <Badge variant="secondary" className="ml-1.5 text-xs h-5 px-1.5 bg-teal-100 text-teal-700">1</Badge>
              )}
            </Button>
            {someSelected && isAdmin && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600 whitespace-nowrap">{selectedProducts.size} selected</span>
                <Button variant="destructive" size="sm" onClick={handleBulkDelete} disabled={isDeleting} data-testid="bulk-delete-btn">
                  <Trash2 className="w-4 h-4 mr-1" />
                  {isDeleting ? 'Deleting...' : 'Delete Selected'}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setSelectedProducts(new Set())} data-testid="clear-selection-btn">
                  Clear
                </Button>
              </div>
            )}
          </div>

          {/* Expandable filter row */}
          {showFilters && (
            <div className="flex items-center gap-3 pt-1 border-t border-slate-100">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-slate-500 whitespace-nowrap">Brand:</Label>
                <Select value={brandFilter} onValueChange={setBrandFilter}>
                  <SelectTrigger className="w-48 h-8 text-sm" data-testid="brand-filter">
                    <SelectValue placeholder="All Brands" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Brands</SelectItem>
                    {brands.map(b => (
                      <SelectItem key={b} value={b}>{b}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {brandFilter !== 'all' && (
                <Button variant="ghost" size="sm" onClick={() => setBrandFilter('all')} className="text-xs text-slate-500">
                  Clear filters
                </Button>
              )}
              <span className="text-xs text-slate-400 ml-auto">
                {filteredProducts.length} of {products.length} products
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Duplicate alert banner */}
      {duplicateProductIds.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="duplicate-alert-banner">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
          <p className="text-sm text-amber-800">
            <span className="font-semibold">{duplicateProductIds.size} products</span> flagged as duplicates.
            Duplicates are highlighted below.
          </p>
          <Button size="sm" variant="outline" className="ml-auto border-amber-300 text-amber-700" onClick={() => setShowDuplicateModal(true)}>
            Manage Duplicates
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setDuplicateProductIds(new Set())} className="text-amber-600">
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Products Table */}
      <Card className="border border-slate-200">
        <CardContent className="p-0">
          {filteredProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-500">
              <Package className="w-12 h-12 mb-4 text-slate-300" />
              <p className="font-medium">No products found</p>
              <p className="text-sm text-slate-400 mt-1">Add products manually or use bulk import</p>
              {isAdmin && (
                <Button variant="outline" className="mt-4" onClick={() => { resetForm(); setIsDialogOpen(true); }}>
                  Add your first product
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="professional-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox checked={allSelected} onCheckedChange={handleSelectAll} data-testid="select-all-checkbox" />
                    </TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Brand</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>HSN Code</TableHead>
                    <TableHead>GST %</TableHead>
                    <TableHead className="text-right">Purchase Price</TableHead>
                    <TableHead className="text-right">Selling Price</TableHead>
                    {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProducts.map((product) => {
                    const isDup = duplicateProductIds.has(product.id);
                    const isRecent = isRecentProduct(product.created_at);
                    return (
                      <TableRow
                        key={product.id}
                        data-testid={`product-row-${product.id}`}
                        className={cn(
                          selectedProducts.has(product.id) && 'bg-teal-50',
                          isDup && !selectedProducts.has(product.id) && 'bg-amber-50/60 border-l-2 border-l-amber-400'
                        )}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedProducts.has(product.id)}
                            onCheckedChange={(checked) => handleSelectProduct(product.id, checked)}
                            data-testid={`select-product-${product.id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">{product.name}</span>
                            {isRecent && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 bg-emerald-50 text-emerald-700 border-emerald-200" data-testid={`recent-tag-${product.id}`}>
                                New
                              </Badge>
                            )}
                            {isDup && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 bg-amber-50 text-amber-700 border-amber-200" data-testid={`duplicate-tag-${product.id}`}>
                                Duplicate
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{product.brand}</TableCell>
                        <TableCell>{product.size}</TableCell>
                        <TableCell className="tabular-nums">{product.hsn_code}</TableCell>
                        <TableCell className="tabular-nums">{product.gst_percentage}%</TableCell>
                        <TableCell className="text-right tabular-nums">{formatCurrency(product.purchase_price)}</TableCell>
                        <TableCell className="text-right tabular-nums font-semibold text-slate-900">{formatCurrency(product.selling_price)}</TableCell>
                        {isAdmin && (
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" data-testid={`product-actions-${product.id}`}>
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEdit(product)} data-testid={`edit-product-${product.id}`}>
                                  <Pencil className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDeleteClick(product)} className="text-red-600" data-testid={`delete-product-${product.id}`}>
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ===== Add/Edit Product Dialog ===== */}
      {isAdmin && (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className={editingProduct ? 'sm:max-w-3xl max-h-[90vh] overflow-y-auto' : 'sm:max-w-lg'}>
            <DialogHeader>
              <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Product Name *</Label>
                    <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="product-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="brand">Brand *</Label>
                    <Input id="brand" value={formData.brand} onChange={(e) => setFormData({ ...formData, brand: e.target.value })} required data-testid="product-brand-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="size">Size *</Label>
                    <Input id="size" value={formData.size} onChange={(e) => setFormData({ ...formData, size: e.target.value })} placeholder="e.g., 3.5mm x 10mm" required data-testid="product-size-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="hsn_code">HSN Code *</Label>
                    <Input id="hsn_code" value={formData.hsn_code} onChange={(e) => setFormData({ ...formData, hsn_code: e.target.value })} placeholder="e.g., 90211000" required data-testid="product-hsn-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="buyer_item_code">Buyer Item Code <span className="text-xs text-slate-500">(optional, may be shared across SKUs)</span></Label>
                  <Input id="buyer_item_code" value={formData.buyer_item_code} onChange={(e) => setFormData({ ...formData, buyer_item_code: e.target.value })} placeholder="e.g., DEN-IMP-002" data-testid="product-buyer-item-code-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="diameter_mm">Diameter (mm) <span className="text-xs text-slate-500">(optional)</span></Label>
                    <Input id="diameter_mm" type="number" step="0.01" min="0" value={formData.diameter_mm} onChange={(e) => setFormData({ ...formData, diameter_mm: e.target.value })} placeholder="e.g., 3.75" data-testid="product-diameter-input" />
                    <p className="text-[10px] text-slate-500">Overrides value parsed from Size for Buyer Item auto-mapping.</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="length_mm">Length (mm) <span className="text-xs text-slate-500">(optional)</span></Label>
                    <Input id="length_mm" type="number" step="0.01" min="0" value={formData.length_mm} onChange={(e) => setFormData({ ...formData, length_mm: e.target.value })} placeholder="e.g., 11.5" data-testid="product-length-input" />
                    <p className="text-[10px] text-slate-500">Leave blank to fall back to Size parsing.</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gst_percentage">GST Percentage *</Label>
                  <Input id="gst_percentage" type="number" value={formData.gst_percentage} onChange={(e) => setFormData({ ...formData, gst_percentage: e.target.value })} required data-testid="product-gst-input" />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="purchase_price">Purchase Price (*)</Label>
                    <Input
                      id="purchase_price"
                      type="number"
                      step="0.01"
                      value={formData.purchase_price}
                      onChange={(e) => handlePurchasePriceChange(e.target.value)}
                      required
                      data-testid="product-purchase-price-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="margin_percentage">Markup %</Label>
                    <Input
                      id="margin_percentage"
                      type="number"
                      step="0.01"
                      value={formData.margin_percentage}
                      onChange={(e) => handleMarginChange(e.target.value)}
                      placeholder="e.g. 20"
                      data-testid="product-margin-input"
                    />
                    <p className="text-[10px] text-slate-500">Selling = Purchase × (1 + Markup/100). Edits sync both ways.</p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="selling_price">Selling Price (*)</Label>
                      {formData.selling_price_manually_edited && (
                        <button
                          type="button"
                          onClick={resetManualFlag}
                          className="text-[10px] text-teal-600 hover:text-teal-800 underline"
                          data-testid="reset-manual-price-btn"
                        >
                          Use formula
                        </button>
                      )}
                    </div>
                    <Input
                      id="selling_price"
                      type="number"
                      step="0.01"
                      value={formData.selling_price}
                      onChange={(e) => handleSellingPriceChange(e.target.value)}
                      required
                      data-testid="product-selling-price-input"
                      className={formData.selling_price_manually_edited ? 'border-amber-400 ring-1 ring-amber-300' : ''}
                    />
                    <p className={`text-[10px] ${formData.selling_price_manually_edited ? 'text-amber-700 font-medium' : 'text-slate-500'}`}>
                      {formData.selling_price_manually_edited
                        ? 'Manual override — future purchases will NOT update this price.'
                        : 'Auto-calculated from Purchase × Margin.'}
                    </p>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="btn-primary" data-testid="save-product-btn">{editingProduct ? 'Update' : 'Create'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* ===== Delete Confirmation Dialog ===== */}

      <Dialog open={showDeleteConfirm} onOpenChange={(open) => { if (!open) { setShowDeleteConfirm(false); setDeleteTarget(null); setUsageInfo(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {usageInfo?.has_links ? (
                <><AlertTriangle className="w-5 h-5 text-amber-500" />Product Has Linked Records</>
              ) : (
                <>Delete Product</>
              )}
            </DialogTitle>
            <DialogDescription>
              {deleteTarget?.name} — {deleteTarget?.brand}
            </DialogDescription>
          </DialogHeader>
          {checkingUsage ? (
            <div className="flex items-center justify-center py-6">
              <div className="spinner" />
              <span className="ml-2 text-sm text-slate-500">Checking linked records...</span>
            </div>
          ) : usageInfo?.has_links ? (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium text-amber-800">This product is linked to:</p>
                {usageInfo.purchases > 0 && <p className="text-sm text-amber-700">- {usageInfo.purchases} purchase record(s)</p>}
                {usageInfo.sales > 0 && <p className="text-sm text-amber-700">- {usageInfo.sales} sales invoice(s)</p>}
                {usageInfo.inventory > 0 && <p className="text-sm text-amber-700">- {usageInfo.inventory} inventory entry(ies)</p>}
              </div>
              <p className="text-sm text-slate-600">
                Deleting this product may affect historical records. Consider using <strong>Merge</strong> instead via the Detect Duplicates feature.
              </p>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
                <Button variant="destructive" onClick={() => handleConfirmDelete(true)} data-testid="force-delete-btn">
                  Force Delete
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">This product has no linked records. Safe to delete.</p>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
                <Button variant="destructive" onClick={() => handleConfirmDelete(false)} data-testid="confirm-delete-btn">
                  Delete
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== Duplicate Detection Modal ===== */}
      <Dialog open={showDuplicateModal} onOpenChange={setShowDuplicateModal}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="w-5 h-5 text-amber-600" />
              Duplicate Products — {duplicateStats?.duplicate_clusters || 0} Cluster(s) Found
            </DialogTitle>
            <DialogDescription>
              {duplicateStats?.total_duplicates || 0} products across {duplicateStats?.duplicate_clusters || 0} groups share the same brand + name.
              Select a primary to keep and mark others for merge or delete.
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="flex-1 px-1">
            <div className="space-y-6 py-4">
              {duplicateClusters.map((cluster, clusterIdx) => (
                <div key={clusterIdx} className="border border-amber-200 rounded-lg overflow-hidden" data-testid={`duplicate-cluster-${clusterIdx}`}>
                  <div className="bg-amber-50 px-4 py-3 flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-slate-800">{cluster.brand} — {cluster.name}</p>
                      <p className="text-xs text-slate-500">{cluster.count} entries found</p>
                    </div>
                    {primarySelections[clusterIdx] && (
                      <Badge className="bg-teal-100 text-teal-700 border-teal-200">Primary selected</Badge>
                    )}
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="w-12">Select</TableHead>
                        <TableHead className="w-20">Primary</TableHead>
                        <TableHead>Size</TableHead>
                        <TableHead>HSN</TableHead>
                        <TableHead>GST%</TableHead>
                        <TableHead className="text-right">Purchase</TableHead>
                        <TableHead className="text-right">Selling</TableHead>
                        <TableHead>Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cluster.products.map((p) => {
                        const isPrimary = primarySelections[clusterIdx] === p.id;
                        const isSelected = selectedDuplicates.has(p.id);
                        return (
                          <TableRow
                            key={p.id}
                            className={cn(
                              isPrimary && 'bg-teal-50',
                              isSelected && !isPrimary && 'bg-red-50'
                            )}
                          >
                            <TableCell>
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => toggleDuplicateSelection(p.id, clusterIdx)}
                                disabled={isPrimary}
                                data-testid={`dup-select-${p.id}`}
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                variant={isPrimary ? 'default' : 'outline'}
                                size="sm"
                                className={cn('h-7 text-xs', isPrimary ? 'bg-teal-600 hover:bg-teal-700' : '')}
                                onClick={() => handleSetPrimary(clusterIdx, p.id)}
                                data-testid={`set-primary-${p.id}`}
                              >
                                {isPrimary ? 'Primary' : 'Set'}
                              </Button>
                            </TableCell>
                            <TableCell>{p.size || '-'}</TableCell>
                            <TableCell className="tabular-nums">{p.hsn_code || '-'}</TableCell>
                            <TableCell>{p.gst_percentage}%</TableCell>
                            <TableCell className="text-right tabular-nums">{formatCurrency(p.purchase_price)}</TableCell>
                            <TableCell className="text-right tabular-nums">{formatCurrency(p.selling_price)}</TableCell>
                            <TableCell className="text-xs text-slate-500">
                              {p.created_at ? new Date(p.created_at).toLocaleDateString() : '-'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ))}
            </div>
          </ScrollArea>

          <DialogFooter className="pt-4 border-t gap-2 flex-wrap">
            <span className="text-sm text-slate-500 mr-auto">{selectedDuplicates.size} duplicate(s) selected</span>
            <Button variant="outline" onClick={() => { setShowDuplicateModal(false); }}>Close</Button>
            <Button
              variant="destructive"
              onClick={handleDeleteSelectedDuplicates}
              disabled={selectedDuplicates.size === 0 || merging}
              data-testid="delete-duplicates-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete Selected
            </Button>
            <Button
              className="bg-teal-600 hover:bg-teal-700"
              onClick={handleMergeDuplicates}
              disabled={selectedDuplicates.size === 0 || merging}
              data-testid="merge-duplicates-btn"
            >
              <Merge className="w-4 h-4 mr-1" />
              {merging ? 'Merging...' : 'Merge into Primary'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Bulk Import Dialog ===== */}
      {isAdmin && (
        <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
          <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-teal-600" />
                Bulk Import Products
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="p-4">
                  <h4 className="font-semibold text-blue-800 mb-2">Excel File Format</h4>
                  <p className="text-sm text-blue-700 mb-3">Your Excel file should have the following columns:</p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {['Product Name', 'Brand', 'Size', 'HSN Code', 'GST %', 'Purchase Price', 'Selling Price'].map(col => (
                      <span key={col} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">{col}</span>
                    ))}
                  </div>
                  <Button variant="outline" size="sm" onClick={downloadTemplate}>
                    <Download className="w-4 h-4 mr-1" />
                    Download Template
                  </Button>
                </CardContent>
              </Card>
              <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
                <input type="file" accept=".xlsx,.xls" onChange={handleFileUpload} ref={fileInputRef} className="hidden" id="excel-upload" />
                <label htmlFor="excel-upload" className="cursor-pointer">
                  <Upload className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-sm text-slate-600 font-medium">Click to upload Excel file</p>
                  <p className="text-xs text-slate-400 mt-1">Supports .xlsx and .xls files</p>
                </label>
              </div>
              {importedProducts.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-slate-900">
                      Preview ({importedProducts.filter(p => p.valid).length} valid / {importedProducts.length} total)
                    </h4>
                    <Button variant="ghost" size="sm" onClick={() => setImportedProducts([])}>Clear</Button>
                  </div>
                  <div className="border rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="w-12">Valid</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Brand</TableHead>
                          <TableHead>Size</TableHead>
                          <TableHead>HSN</TableHead>
                          <TableHead className="text-right">GST %</TableHead>
                          <TableHead className="text-right">Purchase</TableHead>
                          <TableHead className="text-right">Selling</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {importedProducts.map((product, index) => (
                          <TableRow key={index} className={!product.valid ? 'bg-red-50' : ''}>
                            <TableCell>
                              {product.valid ? <Check className="w-4 h-4 text-emerald-500" /> : <X className="w-4 h-4 text-red-500" />}
                            </TableCell>
                            <TableCell className="font-medium">{product.name || '-'}</TableCell>
                            <TableCell>{product.brand || '-'}</TableCell>
                            <TableCell>{product.size || '-'}</TableCell>
                            <TableCell className="tabular-nums">{product.hsn_code || '-'}</TableCell>
                            <TableCell className="text-right tabular-nums">{product.gst_percentage}%</TableCell>
                            <TableCell className="text-right tabular-nums">{product.purchase_price ? formatCurrency(product.purchase_price) : '-'}</TableCell>
                            <TableCell className="text-right tabular-nums">{product.selling_price ? formatCurrency(product.selling_price) : '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setIsImportDialogOpen(false); setImportedProducts([]); }}>Cancel</Button>
              <Button
                onClick={handleBulkImport}
                className="btn-primary"
                disabled={importedProducts.filter(p => p.valid).length === 0 || importLoading}
                data-testid="confirm-import-btn"
              >
                {importLoading ? 'Importing...' : `Import ${importedProducts.filter(p => p.valid).length} Products`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Products;
